import React, { useCallback, useMemo } from 'react';
import { FilterMatchMode } from 'primereact/api';
import { Calendar } from 'primereact/calendar';
import { useLanguage } from '../../../../contexts/LanguageContext';

export const ORDER_REQUEST_FILTER_FIELDS = [
    { field: 'dmsOrderNumber' },
    { field: 'brand' },
    { field: 'model' },
    { field: 'matnr' },
    { field: 'maktx' },
    { field: 'quantity' },
    { field: 'deliveryDate', type: 'date' },
    { field: 'paymentDate', type: 'date' },
    { field: 'statusText' },
    { field: 'createdDate', type: 'date' },
    { field: 'createdName' },
    { field: 'modifiedDate', type: 'date' },
    { field: 'modifiedName' }
];

const getDefaultMatchMode = (type) => (type === 'date' ? FilterMatchMode.DATE_IS : FilterMatchMode.CONTAINS);

const useOrderRequestsFilter = ({ activeFilters, onFiltersChange }) => {
    const { th } = useLanguage();

    const textMatchModeOptions = useMemo(
        () => [
            { label: th('filter.startsWith'), value: FilterMatchMode.STARTS_WITH },
            { label: th('filter.contains'), value: FilterMatchMode.CONTAINS },
            { label: th('filter.notContains'), value: FilterMatchMode.NOT_CONTAINS },
            { label: th('filter.endsWith'), value: FilterMatchMode.ENDS_WITH },
            { label: th('filter.equals'), value: FilterMatchMode.EQUALS },
            { label: th('filter.notEquals'), value: FilterMatchMode.NOT_EQUALS }
        ],
        [th]
    );

    const dateMatchModeOptions = useMemo(
        () => [
            { label: th('filter.dateIs'), value: FilterMatchMode.DATE_IS },
            { label: th('filter.dateIsNot'), value: FilterMatchMode.DATE_IS_NOT },
            { label: th('filter.dateBefore'), value: FilterMatchMode.DATE_BEFORE },
            { label: th('filter.dateAfter'), value: FilterMatchMode.DATE_AFTER }
        ],
        [th]
    );

    const filters = useMemo(
        () =>
            ORDER_REQUEST_FILTER_FIELDS.reduce(
                (result, { field, type }) => ({
                    ...result,
                    [field]: {
                        value: activeFilters?.[field] ?? null,
                        matchMode: activeFilters?.[`${field}MatchMode`] ?? getDefaultMatchMode(type)
                    }
                }),
                {}
            ),
        [activeFilters]
    );

    const onFilter = useCallback(
        (event) => {
            const nextFilters = { ...(activeFilters || {}) };

            ORDER_REQUEST_FILTER_FIELDS.forEach(({ field, type }) => {
                nextFilters[field] = event?.filters?.[field]?.value ?? null;
                nextFilters[`${field}MatchMode`] = event?.filters?.[field]?.matchMode ?? getDefaultMatchMode(type);
            });

            onFiltersChange?.(nextFilters);
        },
        [activeFilters, onFiltersChange]
    );

    const dateFilter = (options) => (
        <Calendar
            value={options.value ?? null}
            onChange={(event) => options.filterApplyCallback(event.value ?? null)}
            dateFormat="dd.mm.yy"
            placeholder={th('common.selectDate')}
            className="p-column-filter w-full"
            inputStyle={{ width: '100%', minWidth: '7rem' }}
            showIcon
            showButtonBar
        />
    );

    const getColumnFilterProps = (type) => ({
        filter: true,
        filterPlaceholder: th('common.search'),
        showFilterMenu: true,
        showClearButton: false,
        filterMatchModeOptions: type === 'date' ? dateMatchModeOptions : textMatchModeOptions,
        filterElement: type === 'date' ? dateFilter : undefined
    });

    return { filters, onFilter, getColumnFilterProps };
};

export default useOrderRequestsFilter;
